import React, { useEffect, useMemo, useState } from 'react';
import { Image, Modal } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import ForceBurningPrompt from 'components/admin/ForceBurningPrompt';
import AuthButton from 'components/base/AuthButton';
import PromptButton, { BUTTON_TYPES } from 'components/base/prompt/BaseButtons';
import { CustomTitleWithDropDown } from 'components/customer/CustomTitleWithDropDown';
import { COUPON_TYPES, CustomerStatus } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';

import CloseIcon from 'assets/images/prompt_close_icon.svg';

import './SelectStoreOrUseCouponModal.scss';

const SelectStoreOrUseCouponModal = ({
  customerId,
  coupon,
  show,
  setShow,
  customer,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const couldSelectStore = coupon?.couldSelectStore;

  const [selectStore, setSelectStore] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [showBurnDoubleConfirm, setShowBurnDoubleConfirm] = useState(false);

  const titleText = useMemo(() => {
    const couponLabel =
      coupon?.couponType === COUPON_TYPES.TICKET ? 'ticket' : 'coupon';
    if (confirm) {
      return `Confirm to use ${couponLabel}?`;
    }
    if (couldSelectStore) {
      return `Use ${couponLabel} (or select store only)`;
    }
    return `Use ${couponLabel}`;
  }, [coupon?.couponType, confirm, couldSelectStore]);
  const promptTitle = useMemo(() => {
    if (confirm) {
      return 'Confirm';
    }
    if (coupon?.couponType === COUPON_TYPES.TICKET) {
      return 'Use Ticket';
    } else {
      return 'Use Coupon';
    }
  }, [confirm, coupon?.couponType]);

  const onClose = () => {
    if (confirm) {
      setConfirm(false);
      return;
    }
    setShow(false);
  };

  const buttonAction = (type) => {
    dispatch({
      type: `singleCoupon/${type}`,
      payload: {
        id: coupon?.pk,
        customerId: customerId,
        storeId: selectStore?.value?.pk,
        afterAction: () => {
          setConfirm(false);
          setShow(false);
          history.push(location);
        },
      },
    });
  };

  const primaryButtonAction = () => {
    if (confirm) {
      buttonAction('useCoupon');
      return;
    }
    if (
      [
        CustomerStatus.temporaryEarning,
        CustomerStatus.temporary,
        CustomerStatus.prospect,
      ].includes(customer?.customerStatus)
    ) {
      setShowBurnDoubleConfirm(true);
      return;
    }
    setConfirm(true);
  };

  useEffect(() => {
    if (show) {
      setConfirm(false);
      if (coupon?.store) {
        setSelectStore({
          label: coupon?.store?.displayStoreName,
          value: coupon?.store,
        });
      } else {
        setSelectStore(null);
      }
    }
  }, [show]);

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
      >
        <Modal.Body className={`base-prompt-container customer-detail-prompt`}>
          <Image
            src={CloseIcon}
            className="base-prompt-close-button customer-detail-prompt-close-button"
            onClick={onClose}
          />

          <label className="customer-detail-modal-title">{titleText}</label>

          {confirm ? null : (
            <CustomTitleWithDropDown
              title="Use in store"
              defaultValue={selectStore}
              setValue={setSelectStore}
              source={coupon?.selectedStores || []}
              customItem={(item) => item.displayStoreName}
              loadMoreAction=""
              needFilter={false}
              filterAction=""
            />
          )}

          <div className="base-prompt-buttons customer-detail-prompt-buttons">
            <PromptButton
              title="Cancel"
              action={onClose}
              type={BUTTON_TYPES.secondary}
            />
            {!confirm && couldSelectStore ? (
              <AuthButton
                title="Select store only"
                customClass={
                  'btn-back-button-common select-store-or-use-coupon-button'
                }
                action={() => buttonAction('selectStore')}
                requires={PermissionCodes.changeCoupon}
              />
            ) : null}
            <PromptButton
              title={promptTitle}
              action={primaryButtonAction}
              requires={PermissionCodes.changeCoupon}
              type={BUTTON_TYPES.main}
              disabled={!selectStore?.value?.pk}
            />
          </div>
        </Modal.Body>
      </Modal>
      <ForceBurningPrompt
        customerStatus={customer?.status}
        show={showBurnDoubleConfirm}
        onCancel={() => setShowBurnDoubleConfirm(false)}
        onConfirm={() => {
          setShowBurnDoubleConfirm(false);
          setConfirm(true);
        }}
      />
    </>
  );
};

const mapPropsToState = (state) => ({
  customer: state.customerList.customer,
});

export default connect(mapPropsToState)(SelectStoreOrUseCouponModal);
